/**
 * Value formatting rules from Design Bible 5.3 and 10.3, in the customer
 * register.
 *
 * Identifiers are never truncated and never ellipsized. Timestamps carry an
 * explicit zone label. The reservation countdown is derived from core's
 * `expires_at` rather than from a client-side stopwatch, so a tab that slept
 * shows the real remaining time and not a stale one.
 */

const GROUP_SIZE = 4;
const GROUP_THRESHOLD = 10;

/**
 * Splits a long identifier into groups of four for rendering with a
 * non-selectable separator, so a click-drag copy still yields the raw value.
 * A collector copying a certification number into the grader's own lookup is
 * the single most important copy-paste on this site.
 */
export function groupIdentifier(value: string): readonly string[] {
  if (value.length < GROUP_THRESHOLD || /\s/.test(value)) return [value];
  const groups: string[] = [];
  for (let index = 0; index < value.length; index += GROUP_SIZE) {
    groups.push(value.slice(index, index + GROUP_SIZE));
  }
  return groups;
}

export interface FormattedTimestamp {
  readonly local: string;
  readonly zone: string;
  readonly utc: string;
  readonly valid: boolean;
}

export function formatTimestamp(raw: string | null): FormattedTimestamp | null {
  if (raw === null || raw.length === 0) return null;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return { local: raw, zone: "", utc: raw, valid: false };
  }
  const local = new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(parsed);
  const zone =
    new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
      .formatToParts(parsed)
      .find((part) => part.type === "timeZoneName")?.value ?? "";
  return { local, zone, utc: parsed.toISOString(), valid: true };
}

/** `2026-08-18T10:16:41Z` -> `2026-08-18`, for a machine-readable date attribute. */
export function isoDate(raw: string): string | null {
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().slice(0, 10);
}

export interface Countdown {
  readonly totalSeconds: number;
  readonly display: string;
  readonly expired: boolean;
}

/**
 * Design Bible 11.6: the reservation timer counts down from core's recorded
 * expiry. It is derived from the server timestamp on every tick, never
 * decremented locally, so it cannot drift into claiming time the reservation
 * does not have.
 */
export function countdownTo(expiresAt: string, now: number): Countdown | null {
  const target = new Date(expiresAt).getTime();
  if (Number.isNaN(target)) return null;
  // A positive fraction of a second is still time remaining, not expiry.
  const remaining = Math.max(0, Math.ceil((target - now) / 1_000));
  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  return {
    totalSeconds: remaining,
    display: `${String(minutes)}:${String(seconds).padStart(2, "0")}`,
    expired: remaining === 0
  };
}

/** Announced text for the countdown; 9.4 throttles how often it is spoken. */
export function countdownSentence(countdown: Countdown): string {
  if (countdown.expired) return "The reservation timer has ended.";
  const minutes = Math.floor(countdown.totalSeconds / 60);
  if (minutes >= 1) return `${String(minutes)} minutes left to complete checkout.`;
  return `${String(countdown.totalSeconds)} seconds left to complete checkout.`;
}

/** For the offline banner: "showing the page loaded 3m ago". */
export function relativeAge(loadedAt: number, now: number): string {
  const seconds = Math.max(0, Math.floor((now - loadedAt) / 1_000));
  if (seconds < 60) return `${String(seconds)}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${String(minutes)}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${String(hours)}h ${String(minutes % 60)}m ago`;
}
